import React, { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import moment from 'moment';
import classes from '../pipeline.module.scss';
import Button from '../../../../_common/Form/Buttons/Button';
import Select from '../../../../_common/Form/Selects/Select/Select';
import FilterButton from '../../../../_common/FilterButton/FilterButton';
import { RangeDatePicker } from '../../../../_common/Form/DatePickers/RangeDatePicker/RangeDatePicker';
import { useQuery } from '../../../../../utils/hooks/useQuery';
import Utils from '../../../../../utils/utils';
import { getSelectRepListRequest } from '../../../../../store/actions/users';
import { getHealthRequest } from '../../../../../store/actions/healths';
import { medicationTypeList, sectionList } from '../pipelineHelpers';
import StarAdherence from './Sections/StarAdherence01/StarAdherence';
import GapDays from './Sections/GapDays02/GapDays';
import ShortSupply from './Sections/ShortSupply03/ShortSupply';
import WaitingRx from './Sections/WaitingRx04/WaitingRx';
import Intake from './Sections/Intake04A/Intake';
import ProcessingShipping from './Sections/ProcessingShipping04B/ProcessingShipping';
import FillRates from './Sections/FillRates05/FillRates';
import MailOrder from './Sections/MailOrder06/MailOrder';
import Interventions from './Sections/Interventions07/Interventions';
import Retention from './Sections/Retention08/Retention';
import TransferGaps from './Sections/TransferGaps09/TransferGaps';
import Pivots from './Sections/Pivots10/Pivots';
import Loader from '../../../../_common/Loader/Loader';
import AccountHelpers from '../../../../../utils/accountHelpers';
import { PipelineTooltip } from './PipelineComponents';

const sectionComponents = {
  star_adherence: StarAdherence,
  gap_days: GapDays,
  short_supply: ShortSupply,
  waiting_rx: WaitingRx,
  intake: Intake,
  fulfillment: ProcessingShipping,
  fill_rates: FillRates,
  mail_order: MailOrder,
  interventions: Interventions,
  retention: Retention,
  transfer_gaps: TransferGaps,
  pivots: Pivots,
};

const collectRequestData = (requestData) => {
  const data = Utils.deleteEmptyKeys(requestData);

  if (data.start) {
    if (!data.end) {
      data.end = moment(data.start).add(1, 'day').format('YYYY-MM-DD');
    } else {
      data.end = moment(data.end).format('YYYY-MM-DD');
    }
  }

  return data;
};

const thisMonthStartDay = moment().startOf('month').format('YYYY-MM-DD');
const thisMonthEndDate = moment().format('YYYY-MM-DD');

const limit = 20;

const pdcModeData = [
  {
    title: 'Apply 292 rule',
    key: 'pdcMode',
    value: 'rule_292',
  },
];

const PipelineReports = () => {
  const isAdmin = AccountHelpers.isInRole('Admin');
  const isHealth = AccountHelpers.isInRole('Health');

  const { query, setQuery } = useQuery();

  const dispatch = useDispatch();
  const { selectRepList, pageCount, currentPage } = useSelector((store) => store.users);
  const { selectHealthList, healthTotalPage, healthCurrentPage } = useSelector((store) => store.healths);

  const [filters, setFilterData] = useState({
    start: query.start || thisMonthStartDay,
    end: query.start && !query.end ? '' : query.end || thisMonthEndDate,
    rep: query.rep || '',
    hub: query.hub || '',
    brand: query.brand || '',
    pdcMode: query.pdcMode || '',
  });

  const [params, setParams] = useState(null);

  useEffect(() => {
    (async () => {
      const sendingData = { ...filters };

      if (isAdmin) {
        // dispatch(getSelectRepListRequest({ limit, role: 'Rep' }));
        const { payload } = await dispatch(getHealthRequest({ limit }));

        if (isAdmin && !filters.hub) {
          sendingData.hub = payload?.data?.data?.response?.[0]?.hubId || '';

          setFilterData(sendingData);
        }
      }

      update(sendingData);
    })();
  }, []);

  const nextOptionListRequest = async (page) => {
    await dispatch(getSelectRepListRequest({ page, limit, role: 'Rep' }));
  };

  const nextHealthPlanOptionListRequest = async (page) => {
    await dispatch(getHealthRequest({ page, limit }));
  };

  const changeRequestData = (path, value) => {
    setFilterData((prev) => {
      const newFilters = { ...prev };

      if (path === 'date') {
        const [start, end] = value;

        newFilters.start = start;
        newFilters.end = end;
      } else {
        newFilters[path] = value;
      }

      return newFilters;
    });
  };

  const update = (requestData = filters) => {
    setQuery(Utils.deleteEmptyKeys(requestData));
    setParams(collectRequestData(requestData));
  };

  const clearFilters = () => {
    const sendingData = {
      start: thisMonthStartDay,
      end: thisMonthEndDate,
      rep: '',
      hub: filters.hub || '',
      brand: '',
      pdcMode: '',
    };

    setFilterData(sendingData);
    update(sendingData);
  };

  const scrollToSection = (id) => {
    document.getElementById(id)?.scrollIntoView({ behavior: 'smooth', block: 'start' });
  };

  const sections = isHealth ? sectionList.filter((section) => section.id !== 'pivots') : sectionList;

  return (
    <div className={classes.wrapper}>
      <div className={classes.filters_wrapper}>
        <RangeDatePicker
          currentDate={filters}
          onChangeDate={(date) => changeRequestData('date', date)}
          showLastYear
        />

        {/* <div className={classes.select_wrapper}> */}
        {/*  <Select */}
        {/*    label="All Reps" */}
        {/*    nextOptionListRequest={nextOptionListRequest} */}
        {/*    options={selectRepList} */}
        {/*    totalPage={pageCount} */}
        {/*    currentPage={currentPage} */}
        {/*    limit={limit} */}
        {/*    currentOption={filters.rep} */}
        {/*    onChange={(option) => changeRequestData('rep', option)} */}
        {/*    optionPathname="name" */}
        {/*  /> */}
        {/* </div> */}

        {isAdmin && (
          <div className={classes.select_wrapper}>
            <Select
              label="All health plans"
              nextOptionListRequest={nextHealthPlanOptionListRequest}
              options={selectHealthList}
              totalPage={healthTotalPage}
              currentPage={healthCurrentPage}
              limit={limit}
              currentOption={filters.hub}
              onChange={(option) => changeRequestData('hub', option)}
              optionPathname="hubId"
              hideClearButton
            />
          </div>
        )}

        <div className={classes.select_wrapper}>
          <Select
            label="All drug classes"
            options={medicationTypeList}
            currentOption={filters.brand}
            onChange={(option) => changeRequestData('brand', option)}
          />
        </div>

        <div className={classes.pdc_wrapper}>
          <FilterButton
            activeValue={filters.pdcMode}
            buttonsData={pdcModeData}
            onClick={(key, value) => changeRequestData(key, filters.pdcMode === value ? '' : value)}
          />
        </div>

        <div className={classes.btns_wrapper}>
          <Button
            title="Update"
            onClick={() => update()}
          />

          <Button
            title="Clear"
            clearButton
            onClick={clearFilters}
          />
        </div>
      </div>

      {!params
        ? (
          <div className={classes.loader_wrapper}>
            <Loader />
          </div>
        )
        : (
          <>
            <div className={classes.section_nav}>
              {sections.map((section) => (
                <button
                  type="button"
                  key={section.id}
                  className={classes.section_nav_item}
                  onClick={() => scrollToSection(section.id)}
                >
                  <b>{section.num}</b>

                  {section.nav}
                </button>
              ))}
            </div>

            <div className={classes.report_wrapper}>
              {sections.map((section) => {
                const Section = sectionComponents[section.id];

                return (
                  <Section
                    key={section.id}
                    section={section}
                    params={params}
                  />
                );
              })}
            </div>
          </>
        )}

      <PipelineTooltip />
    </div>
  );
};

export default PipelineReports;
